import {
  isPreviewEmailOverrideEnvironment as isPreviewEmailOverrideEnvironmentFromDeployEnv,
  resolveAdminDeployEnvironment,
  shouldShowEnvironmentSafetyBadges,
} from "@/utils/deployEnvironment";

export const PREVIEW_EMAIL_OVERRIDE_STORAGE_KEY = "hc-preview-email-override";

export const PREVIEW_EMAIL_OVERRIDE_HEADER = "x-hc-preview-email-override";
export const PREVIEW_EMAIL_OVERRIDE_UPDATED_EVENT =
  "hc-preview-email-override-updated";
export const PREVIEW_EMAIL_OVERRIDE_OPEN_EVENT =
  "hc-preview-email-override-open";
export const PREVIEW_EMAIL_OVERRIDE_MISSING_CODE =
  "PREVIEW_EMAIL_OVERRIDE_MISSING";
export const PREVIEW_EMAIL_OVERRIDE_MISSING_MESSAGE =
  "Outbound email blocked in safe mode. Set the email override before sending.";

const LEGACY_PREVIEW_EMAIL_OVERRIDE_STORAGE_KEY =
  "hc-preview-email-override-session";

const normalizePreviewEmail = (value: string): string =>
  value.trim().toLowerCase();

const isValidEmail = (value: string): boolean =>
  /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value);

export const getConfiguredPreviewEmailOverride = (): string | null => {
  const configuredValue =
    import.meta.env.ADMIN_EMAIL_OVERIDE ??
    import.meta.env.ADMIN_EMAIL_OVERRIDE ??
    "";
  const normalizedConfiguredValue = normalizePreviewEmail(configuredValue);

  return isValidEmail(normalizedConfiguredValue)
    ? normalizedConfiguredValue
    : null;
};

const getLocalStorage = (): Storage | undefined => {
  if (typeof window === "undefined") {
    return undefined;
  }

  return window.localStorage;
};

const getLegacySessionStorage = (): Storage | undefined => {
  if (typeof window === "undefined") {
    return undefined;
  }

  return window.sessionStorage;
};

const dispatchPreviewEmailOverrideUpdatedEvent = (): void => {
  if (typeof window === "undefined") {
    return;
  }

  window.dispatchEvent(new CustomEvent(PREVIEW_EMAIL_OVERRIDE_UPDATED_EVENT));
};

export const requestPreviewEmailOverrideDialog = (): void => {
  if (typeof window === "undefined") {
    return;
  }

  window.dispatchEvent(new CustomEvent(PREVIEW_EMAIL_OVERRIDE_OPEN_EVENT));
};

export const ensurePreviewEmailOverrideBeforeEmailSend = (): boolean => {
  if (!isPreviewEmailOverrideEnvironment()) {
    return true;
  }

  if (getStoredPreviewEmailOverride()) {
    return true;
  }

  requestPreviewEmailOverrideDialog();
  return false;
};

const readStringField = (source: unknown, key: string): string | null => {
  if (typeof source !== "object" || source === null || Array.isArray(source)) {
    return null;
  }

  const value = (source as Record<string, unknown>)[key];
  return typeof value === "string" ? value : null;
};

export const requestPreviewEmailOverrideForMissingError = (
  error: unknown,
): boolean => {
  const errorData =
    typeof error === "object" && error !== null && !Array.isArray(error)
      ? (error as Record<string, unknown>).data
      : null;
  const code =
    readStringField(error, "code") ??
    readStringField(error, "errorCode") ??
    readStringField(errorData, "code") ??
    readStringField(errorData, "errorCode");
  const message =
    readStringField(error, "message") ??
    readStringField(error, "detail") ??
    readStringField(errorData, "message") ??
    readStringField(errorData, "detail");

  if (
    code === PREVIEW_EMAIL_OVERRIDE_MISSING_CODE ||
    message === PREVIEW_EMAIL_OVERRIDE_MISSING_MESSAGE
  ) {
    requestPreviewEmailOverrideDialog();
    return true;
  }

  return false;
};

export const isPreviewEmailOverrideEnvironment = (): boolean =>
  isPreviewEmailOverrideEnvironmentFromDeployEnv({
    env: import.meta.env,
  });

export const isPreviewEnvironment = isPreviewEmailOverrideEnvironment;

export { resolveAdminDeployEnvironment, shouldShowEnvironmentSafetyBadges };

export const getStoredPreviewEmailOverride = (): string | null => {
  const localStorage = getLocalStorage();
  if (localStorage) {
    const localValue =
      localStorage.getItem(PREVIEW_EMAIL_OVERRIDE_STORAGE_KEY) ?? "";
    const normalizedLocalValue = normalizePreviewEmail(localValue);
    if (isValidEmail(normalizedLocalValue)) {
      return normalizedLocalValue;
    }
  }

  const legacySessionStorage = getLegacySessionStorage();
  const legacySessionValue =
    legacySessionStorage?.getItem(PREVIEW_EMAIL_OVERRIDE_STORAGE_KEY) ??
    legacySessionStorage?.getItem(LEGACY_PREVIEW_EMAIL_OVERRIDE_STORAGE_KEY) ??
    "";
  const normalizedLegacyValue = normalizePreviewEmail(legacySessionValue);

  if (!isValidEmail(normalizedLegacyValue)) {
    return getConfiguredPreviewEmailOverride();
  }

  // One-time migration for older browser tabs that saved this in sessionStorage.
  localStorage?.setItem(
    PREVIEW_EMAIL_OVERRIDE_STORAGE_KEY,
    normalizedLegacyValue,
  );
  legacySessionStorage?.removeItem(PREVIEW_EMAIL_OVERRIDE_STORAGE_KEY);
  legacySessionStorage?.removeItem(LEGACY_PREVIEW_EMAIL_OVERRIDE_STORAGE_KEY);
  return normalizedLegacyValue;
};

export const setStoredPreviewEmailOverride = (rawEmail: string): void => {
  const localStorage = getLocalStorage();
  if (!localStorage) {
    return;
  }

  const normalized = normalizePreviewEmail(rawEmail);
  if (!normalized || !isValidEmail(normalized)) {
    localStorage.removeItem(PREVIEW_EMAIL_OVERRIDE_STORAGE_KEY);
    return;
  }

  localStorage.setItem(PREVIEW_EMAIL_OVERRIDE_STORAGE_KEY, normalized);
  getLegacySessionStorage()?.removeItem(PREVIEW_EMAIL_OVERRIDE_STORAGE_KEY);
  getLegacySessionStorage()?.removeItem(
    LEGACY_PREVIEW_EMAIL_OVERRIDE_STORAGE_KEY,
  );
  dispatchPreviewEmailOverrideUpdatedEvent();
};

export const clearStoredPreviewEmailOverride = (): void => {
  const localStorage = getLocalStorage();
  if (!localStorage) {
    return;
  }

  localStorage.removeItem(PREVIEW_EMAIL_OVERRIDE_STORAGE_KEY);
  getLegacySessionStorage()?.removeItem(PREVIEW_EMAIL_OVERRIDE_STORAGE_KEY);
  getLegacySessionStorage()?.removeItem(
    LEGACY_PREVIEW_EMAIL_OVERRIDE_STORAGE_KEY,
  );
  dispatchPreviewEmailOverrideUpdatedEvent();
};
